import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Paper,
  Skeleton,
  Divider,
  Button,
  Breadcrumbs,
  Link,
  alpha,
  CircularProgress,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HomeIcon from '@mui/icons-material/Home';
import { notificationAPI, friendsAPI } from '../services/api';
import { resolveNotificationUrl } from '../services/notificationNavigation';
import { useAuth } from '../context/AuthContext';

const formatTimeAgo = (timestamp) => {
  const normalized =
    typeof timestamp === 'number'
      ? timestamp
      : Math.floor(new Date(timestamp).getTime() / 1000);
  if (!normalized || Number.isNaN(normalized)) return '';
  const now = Math.floor(Date.now() / 1000);
  const diff = now - normalized;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(normalized * 1000).toLocaleDateString();
};

const isUnreadNotification = (notif) => notif?.readAt == null && notif?.seen !== 1;
const getNotificationActor = (notif) => ({
  name: notif?.actor?.name || notif?.writer?.name || 'Someone',
  image: notif?.actor?.image || notif?.writer?.image,
});
const getNotificationActionText = (notif) =>
  notif?.actionText ||
  (() => {
    const type = String(notif?.type || '');
    const targetType = String(notif?.target?.targetType || notif?.target?.type || '').toLowerCase();

    if (type === 'comment.like') {
      if (targetType === 'lesson' || notif?.target?.courseId || notif?.metadata?.courseId) {
        return 'liked your comment on a lesson';
      }
      return 'liked your comment';
    }

    return (
      {
        'post.like': 'liked your post',
        'post.comment': 'commented on your post',
        'comment.created': 'commented on your post',
        'post.comment_reply': 'replied to your comment',
        'comment.reply': 'replied to your comment',
        'lesson.added': 'added a new lesson',
        'lesson.comment': 'commented on a lesson',
        'lesson.comment_reply': 'replied to your lesson comment',
        'payment.created': 'submitted a payment',
        'payment.activated': 'activated your subscription',
        'chat.message': 'sent you a message',
        'chat.support': 'sent a support message',
        'friend.request': 'sent you a friend request',
        'friend.accept': 'accepted your friend request',
      }[type] || 'sent you a notification'
    );
  })();

const getNotificationSubtitle = (notif) => {
  const type = String(notif?.type || '');
  if (type === 'chat.message') return 'Tap to open chat';
  if (type === 'payment.activated') return 'Tap to open My Learning';
  if (type === 'payment.created') return 'Tap to view payment';
  if (type.startsWith('lesson.') || (type === 'comment.like' && (notif?.target?.courseId || notif?.metadata?.courseId))) {
    return 'Tap to view lesson';
  }
  if (type.startsWith('friend.')) return 'Tap to view profile';
  if (type.startsWith('post.') || type.startsWith('comment.')) return 'Tap to view post';
  return null;
};
const getNotificationTimestamp = (notif) => notif?.createdAt || notif?.time;

function Notifications() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingRead, setMarkingRead] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [notifRes, friendRes] = await Promise.all([
        notificationAPI.get({ limit: 150 }),
        friendsAPI.getRequests('english').catch(() => ({ data: { request: [] } })),
      ]);
      setNotifications(notifRes.data || []);
      setUnreadCount(notifRes.meta?.unreadCount || 0);
      setFriendRequests(friendRes.data?.request || []);
    } catch (err) {
      if (err.message !== 'Not authenticated') {
        console.error('Failed to fetch notifications:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: '/notifications' }, replace: true });
      return;
    }
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, authLoading, navigate, fetchData]);

  const handleMarkAllRead = async () => {
    if (markingRead || unreadCount === 0) return;
    setMarkingRead(true);
    try {
      await notificationAPI.markRead();
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, seen: 1 })));
    } catch (err) {
      console.error('Failed to mark notifications as read:', err);
    } finally {
      setMarkingRead(false);
    }
  };

  const handleNotificationClick = async (notif) => {
    if (notif.id && isUnreadNotification(notif)) {
      try {
        await notificationAPI.markOneRead(notif.id);
        setUnreadCount((prev) => Math.max(0, prev - 1));
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, seen: 1, readAt: n.readAt || new Date().toISOString() } : n))
        );
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }
    const url = resolveNotificationUrl(notif);
    if (url) navigate(url);
  };

  const handleFriendRequestClick = (req) => {
    const userId = String(req?.userId || req?.otherId || req?.id || req?.phone || '').trim();
    if (userId) navigate(`/profile/${userId}`);
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const totalCount = notifications.length + friendRequests.length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="xl" sx={{ py: 2, px: { xs: 1.5, sm: 2.5, md: 3 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          sx={{ mb: 2 }}
          separator={<ChevronRightIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
        >
          <Link
            component={RouterLink}
            to="/"
            underline="hover"
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              fontSize: '0.8125rem',
              fontWeight: 500,
              '&:hover': { color: 'primary.main' },
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Home
          </Link>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.primary',
              fontSize: '0.8125rem',
              fontWeight: 600,
            }}
          >
            <NotificationsIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Notifications
          </Typography>
        </Breadcrumbs>

        {/* Page Header - same as Explore */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.6px', mb: 0.25, fontSize: { xs: '1.15rem', sm: '1.35rem' } }}>
                Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.25 }}>
                Friend requests and post activity — likes, comments and more
              </Typography>
            </Box>
            {totalCount > 0 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={markingRead ? <CircularProgress size={14} /> : <DoneAllIcon fontSize="small" />}
                onClick={handleMarkAllRead}
                disabled={markingRead || unreadCount === 0}
                sx={{ textTransform: 'none', py: 0.5 }}
              >
                Mark all read
              </Button>
            )}
          </Box>
        </Box>

      {loading ? (
        [...Array(5)].map((_, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.25, py: 1.25 }}>
            <Skeleton variant="circular" width={36} height={36} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="70%" height={20} />
              <Skeleton width="50%" height={16} sx={{ mt: 1 }} />
            </Box>
          </Box>
        ))
      ) : totalCount === 0 ? (
        <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <NotificationsIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No notifications yet
          </Typography>
          <Typography variant="body2" color="text.disabled">
            When someone likes your post, comments, or sends a friend request, you’ll see it here.
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {friendRequests.length > 0 && (
            <>
              <Box sx={{ px: 1.5, py: 1, bgcolor: alpha('#000', 0.04) }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                  Friend requests
                </Typography>
              </Box>
              {friendRequests.map((req) => (
                <Box
                  key={req.userId || req.phone}
                  onClick={() => handleFriendRequestClick(req)}
                  sx={{
                    display: 'flex',
                    gap: 1.25,
                    px: 1.5,
                    py: 1.25,
                    cursor: 'pointer',
                    borderLeft: '3px solid',
                    borderColor: 'primary.main',
                    bgcolor: alpha('#2e7d32', 0.06),
                    transition: 'background 0.15s ease',
                    '&:hover': { bgcolor: alpha('#000', 0.06) },
                  }}
                >
                  <Avatar
                    src={req.userImage}
                    sx={{ width: 36, height: 36, flexShrink: 0 }}
                  >
                    {(req.userName || '?').charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.25, minWidth: 0 }}>
                        <Typography component="span" fontWeight={600}>
                          {req.userName || 'Someone'}
                        </Typography>{' '}
                        sent you a friend request
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25, display: 'block', lineHeight: 1.2 }}>
                      Tap to view profile and accept or decline
                    </Typography>
                  </Box>
                  <PersonAddIcon sx={{ color: 'primary.main', fontSize: 22 }} />
                </Box>
              ))}
              <Divider />
            </>
          )}

          {notifications.length > 0 && (
            <>
              {friendRequests.length > 0 && (
                <Box sx={{ px: 1.5, py: 1, bgcolor: alpha('#000', 0.04) }}>
                  <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                    Post activity
                  </Typography>
                </Box>
              )}
              {notifications.map((notif) => {
                const unread = isUnreadNotification(notif);
                const actor = getNotificationActor(notif);
                const actionText = getNotificationActionText(notif);
                const subtitleText = notif?.target?.title || getNotificationSubtitle(notif);
                const timestamp = getNotificationTimestamp(notif);
                return <Box
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  sx={{
                    display: 'flex',
                    gap: 1.25,
                    px: 1.5,
                    py: 1.25,
                    cursor: 'pointer',
                    bgcolor: unread ? alpha('#2e7d32', 0.04) : 'transparent',
                    borderLeft: unread ? '3px solid' : 'none',
                    borderColor: 'primary.main',
                    transition: 'background 0.15s ease',
                    '&:hover': { bgcolor: alpha('#000', 0.04) },
                  }}
                >
                  <Avatar
                    src={actor.image}
                    sx={{ width: 36, height: 36, flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.25, minWidth: 0 }}>
                        <Typography component="span" fontWeight={600}>
                          {actor.name}
                        </Typography>{' '}
                        {actionText}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, lineHeight: 1.2 }}>
                        {formatTimeAgo(timestamp)}
                      </Typography>
                    </Box>
                    {subtitleText && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          mt: 0.25,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          lineHeight: 1.2,
                        }}
                      >
                        {subtitleText}
                      </Typography>
                    )}
                  </Box>
                </Box>;
              })}
            </>
          )}
        </Paper>
      )}
      </Container>
    </Box>
  );
}

export default Notifications;
